import {Link} from "react-router-dom";
import {formatMoneyDisplay, type I18nKey, type ProductVolumeVariant} from "@zaaaz/shared";
import {useI18n} from "./I18nProvider";

type Props = {
  variants: ProductVolumeVariant[];
  pricesHidden?: boolean;
};

function kindLabel(kind: ProductVolumeVariant["kind"], t: (key: I18nKey) => string): string | null {
  if(kind === "tester") return t("Tester");
  if(kind === "sample") return t("Sample");
  if(kind === "det") return t("Damaged");
  return null;
}

function discountBadge(price: number, oldPrice: number | null): string | null {
  if(!oldPrice || oldPrice <= price) return null;
  const pct = Math.round((1 - price / oldPrice) * 100);
  if(pct <= 0) return null;
  return `-${pct}%`;
}

export function PdpVolumeVariants({variants, pricesHidden = false}: Props) {
  const {t, currency, eurRate} = useI18n();
  if(!variants || variants.length < 2) return null;

  return (
    <div className="pvVolumeVariantsBlock">
      <p className="pvVolumeVariants__title">{t("Volume")}</p>
      <div className="pvVolumeVariants" role="list" aria-label={t("Volume")}>
      {variants.map((v) => {
        const badge = !pricesHidden ? discountBadge(v.price, v.oldPrice) : null;
        const kind = kindLabel(v.kind, t);
        const priceLabel = pricesHidden ? t("Sign in to see price") : formatMoneyDisplay(v.price, currency, eurRate);
        const content = (
          <>
            {kind ? <span className="pvVolumeVariant__kind">{kind}</span> : null}
            <span className="pvVolumeVariant__label">{v.label}</span>
            {!pricesHidden ? (
            <span className="pvVolumeVariant__price">
              {v.oldPrice && v.oldPrice > v.price ? (
                <span className="pvVolumeVariant__old">{formatMoneyDisplay(v.oldPrice, currency, eurRate)}</span>
              ) : null}
              {priceLabel}
            </span>
            ) : (
              <span className="pvVolumeVariant__price pvVolumeVariant__price--hidden">{priceLabel}</span>
            )}
            {badge ? <span className="pvVolumeVariant__badge">{badge}</span> : null}
            {!v.inStock ? <span className="pvVolumeVariant__badge pvVolumeVariant__badge--oos">{t("Out of stock")}</span> : null}
          </>
        );

        if(v.isCurrent) {
          return (
            <div key={v.id} className="pvVolumeVariant is-current" role="listitem" aria-current="true">
              {content}
            </div>
          );
        }

        return (
          <Link key={v.id} to={v.url || `/p/${v.slug || v.id}`} className="pvVolumeVariant" role="listitem">
            {content}
          </Link>
        );
      })}
      </div>
    </div>
  );
}
